﻿import { useEffect } from 'react';

import { NotificationContextProvider, useNotification } from 'app/notifications';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { Editor, EditorTools } from '@progress/kendo-react-editor';
import { Card, Col, Container, Fade, Row } from 'react-bootstrap';
import { FeedbackApi, FeedbackCategory } from 'apis/FeedbackApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSuperState } from 'hooks';

import 'styles/global-styles.scss';
import 'styles/kendo-default-theme-with-overrides.scss';

const { AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, Italic, OrderedList, Redo, Strikethrough, Underline, Undo, UnorderedList } = EditorTools;

interface FeedbackObj {
	category: undefined | { Id: number; Name: string };
	feedbackMessage: string;
}

export const Feedback = () => {

    const [state, setState] = useSuperState({
        feedbackCategoryOptions: [] as FeedbackCategory[],
        feedbackLocation: '' as string,
        feedbackObj: { category: undefined, feedbackMessage: '' } as FeedbackObj,
        successfulSubmission: false
    });
    const { feedbackCategoryOptions, feedbackLocation, feedbackObj, successfulSubmission } = state;

    const { catchWithToast } = useNotification();
    const { formatMessage } = useIntl();

    const defaultFeedbackCategory = { Id: 0, Name: formatMessage({ id: 'choose_one.e57d2911', defaultMessage: 'Choose one', description: 'formulario' }) };

    const submitFeedbackOnClick = async () => {

        if (feedbackObj.category && feedbackObj.category.Id) {

            try {

                const feedbackRowId = await FeedbackApi.SubmitFeedback(feedbackObj.category?.Id, feedbackLocation, feedbackObj.feedbackMessage.trim());
                if (feedbackRowId > 0) {

                    setState({ successfulSubmission: true });

                }

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_submit_feedback.1a887385', defaultMessage: 'Failed to submit feedback.' }))(e);

            }

        }

    };

    useEffect(() => {

        const params = new URLSearchParams(window.location.search);
        const loc = params.get('feedbackLocation');
        if (loc) {

            setState({ feedbackLocation: loc });

        }

        const loadCategories = async () => {

            try {

                const val = await FeedbackApi.GetFeedbackCategories();
                setState({ feedbackCategoryOptions: val });

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_load_feedback_categories.628d7e73', defaultMessage: 'Failed to load feedback categories.' }))(e);

            }

        };
        loadCategories();

    }, []);

    useEffect(() => {

        if (successfulSubmission) {

            setTimeout(() => {

                window.close();

            }, 5000);

        }

    }, [successfulSubmission]);

    return (
        <NotificationContextProvider>
            <Container className="feedback-dialog pt-3">
                <Row
                    className="align-items-center p-3 rounded"
                    style={{ backgroundColor: '#293F54' }}
                >
                    <Col className="col-12 d-flex align-items-center justify-content-between">
                        <img
                            alt={formatMessage({ id: 'logo.3df624e8', defaultMessage: 'Logo' })}
                            src="https://content.gmetrix.net/images/GMetrix/GMetrix-Logo-Reverse.png"
                            style={{ height: 40, objectFit: 'contain', width: 150 }}
                        />
                    </Col>
                </Row>
                {!successfulSubmission && (
                <Row className="d-flex flex-column justify-content-center align-items-center">
                    <Col lg={12} md={12} sm={12} xl={12} xs={12}>
                        <div className="text-black p-3 fw-bold text-center">
                            <FormattedMessage defaultMessage="Please use the form below to submit feedback on any issues that you encounter while using our new features (e.g., broken functionality, stuck pages, typos)." id="please_use_the_form_below_to_submit_feed.a478de69" />
                            <br />
                            <br />
                            <FormattedMessage defaultMessage="Feedback will include account information such as your username, page url, the action you are taking, and other information to provide additional context or your feedback." id="feedback_will_include_account_informatio.2f5b71cb" />
                            <br />
                            <br />
                            <FormattedMessage defaultMessage="If you have a how-to question or an issue specific to your account, please contact" id="if_you_have_a_how_to_question_or_an_issu.48278e6d" />
                            {' '}
                            <a
                                href="https://support.gmetrix.net/support/home"
                                rel="noreferrer"
                                style={{ textAlign: 'center', textDecoration: 'underline', verticalAlign: 'unset' }}
                                target="_blank"
                            >
                                <FormattedMessage defaultMessage="Support" id="support.f39b85ae" />
                            </a>
                            .
                        </div>
                    </Col>
                    <Col className="my-2" lg={12} md={12} sm={12} xl={12} xs={12}>
                        <GMetrixDropdown
                            dataItemKey="Id"
                            dataItems={feedbackCategoryOptions}
                            onChange={dataItem => setState({ feedbackObj: { ...feedbackObj, category: dataItem } })}
                            textField="Name"
                            value={feedbackObj.category ? feedbackObj.category : defaultFeedbackCategory}
                        />
                    </Col>
                    <Col className="my-2 mb-4" lg={12} md={12} sm={12} xl={12} xs={12}>
                        <Editor
                            contentStyle={{
                                height: 350
                            }}
                            tools={[
                                [Bold, Italic, Underline, Strikethrough],
                                [AlignLeft, AlignCenter, AlignRight, AlignJustify],
                                [OrderedList, UnorderedList],
                                [Undo, Redo]
                            ]}
                            defaultContent={feedbackObj.feedbackMessage}
                            onChange={e => setState({ feedbackObj: { ...feedbackObj, feedbackMessage: e.html } })}
                        />
                    </Col>

                    <Col
                        className="d-flex justify-content-end" lg={12} md={12} sm={12} xl={12}
                        xs={12}
                    >
                        <GMetrixButton
                            disabled={feedbackObj.feedbackMessage.length === 0 || feedbackObj.category === undefined}
                            onClick={submitFeedbackOnClick}
                            variant="primary"
                        >
                            <FormattedMessage defaultMessage="Submit Feedback" id="submit_feedback.472cefc1" />
                        </GMetrixButton>
                    </Col>
                </Row>
                )}
                <Fade in={successfulSubmission}>
                    <div className="mt-4">
                        <Card
                            bg="light"
                            border="success"
                            className="shadow-sm"
                            text="dark"
                        >
                            <Card.Body className="d-flex align-items-center">
                                <i className="fas fa-check-circle text-success fs-4 me-3"></i>
                                <div>
                                    <Card.Title className="mb-1">
                                        <FormattedMessage defaultMessage="Thank you for your feedback!" id="thank_you_for_your_feedback.932a74b1" />
                                    </Card.Title>
                                    <Card.Text className="mb-0">
                                        <FormattedMessage defaultMessage="Your input helps us improve and deliver a better experience." id="your_input_helps_us_improve_and_deliver.4a9da520" />
                                    </Card.Text>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </Fade>
            </Container>
        </NotificationContextProvider>
    );

};
